// ============================================================
// hitTest — 予約ブロックのヒットテスト（最小ブロック優先）
//
// useCalendarInteraction の mousedown（リサイズ判定）と dblclick で
// 重複していたループを1本化したもの。挙動は現行仕様をそのまま移植:
//   - 重なっている場合はブロック高さが最小の予約を優先
//   - 上下端 RESIZE_ZONE(10px) 以内ならリサイズエッジと判定
//   - 表示対象ステータス or tmp_（新規予約ゴースト）のみ対象
//   - absTop は rangeStart でクランプしない（範囲前開始の予約は負のY）
//
// ※ GridCanvas.tsx の hover 判定は「カスケードX + depth優先」の別仕様
//   （視覚的な最前面を優先）のため意図的にここへは統合していない。
// ============================================================

import { Reservation, GRID_CONFIG } from './types';
import { dateToAbsoluteY } from './useGridGeometry';

/** ブロック上下端のリサイズ判定ゾーン（px） */
export const RESIZE_ZONE = 10;

export interface HitResult {
  reservation: Reservation;
  /** ブロック上端の絶対Y（ボディ座標） */
  absTop: number;
  /** ブロック高さ（overlayMinHeight でクランプ済み） */
  blockHeight: number;
  /** リサイズエッジ（上下端 RESIZE_ZONE 以内）。中央部は null */
  edge: 'top' | 'bottom' | null;
}

/**
 * 指定車両列の bodyY 位置にある予約ブロックを返す（最小ブロック優先）。
 * ヒットなしは null。
 */
export function hitTestSmallestBlock(
  reservations: Reservation[],
  vehicleId: string,
  rangeStart: Date,
  bodyY: number,
  displayStatuses: Set<string>,
): HitResult | null {
  let best: { r: Reservation; absTop: number; blockHeight: number } | null = null;
  let bestBh = Infinity;
  for (const r of reservations) {
    const rvid = String(r.vehicle_id || '');
    if (rvid !== vehicleId) continue;
    // 表示対象のステータスのみ（tmp_ は新規予約ゴースト）
    if (!displayStatuses.has(r.status) && !String(r.id).startsWith('tmp_')) continue;
    const s = new Date(r.start_date);
    const e = new Date(r.end_date);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) continue;
    const absTop = dateToAbsoluteY(rangeStart, s);
    const absBottom = dateToAbsoluteY(rangeStart, e);
    const blockHeight = Math.max(GRID_CONFIG.overlayMinHeight, absBottom - absTop);
    if (bodyY >= absTop && bodyY <= absTop + blockHeight && blockHeight < bestBh) {
      best = { r, absTop, blockHeight };
      bestBh = blockHeight;
    }
  }
  if (!best) return null;

  let edge: 'top' | 'bottom' | null = null;
  if (bodyY - best.absTop < RESIZE_ZONE) {
    edge = 'top';
  } else if (best.absTop + best.blockHeight - bodyY < RESIZE_ZONE) {
    edge = 'bottom';
  }
  return { reservation: best.r, absTop: best.absTop, blockHeight: best.blockHeight, edge };
}
